import * as React from "react";
import {
  redirect,
  RouterProvider,
  createBrowserRouter,
  Navigate,
  useParams
} from "react-router-dom";
import { PrivateRoute } from "./PrivateRoute";
import { GatedAdminFeature, GatedSubscriptionFeature } from "@/pages/Messaging/Integrations/shared/GatedIntegration";
import { BackToIntegrations } from "@/pages/Messaging/Integrations/shared/IntegrationPageWrapper";
import ContactCenter from "@/pages/ContactCenter";
import Home from "@/pages/Home";
import EntraCallback from "@/pages/Auth/EntraCallback";
import LoginCallback from "@/pages/Auth/LoginCallback";
import LoginCallbackError from "@/pages/Auth/LoginCallbackError";
import LoginUnauthorized from "@/pages/Auth/LoginUnauthorized";
import Logout from "@/pages/Auth/Logout";
import LogoutCallback from "@/pages/Auth/LogoutCallback";
import AutoResponses from "@/pages/Messaging/AutoResponses/AutoResponses";
import CannedResponses from "@/pages/Messaging/CannedResponses/CannedResponses";
import TwitterCallback from "@/pages/Messaging/Channels/TwitterCallback";
import IntegrationLog from "@/pages/Messaging/Integrations/Log";
import NetSuite, {
  BackToIntegrationLanding
} from "@/pages/Messaging/Integrations/NetSuite/NetSuite";
import NetSuiteCreate from "@/pages/Messaging/Integrations/NetSuite/NetSuiteCreate";
import NetSuiteCallback from "@/pages/Messaging/Integrations/NetSuite/NetSuiteCallback";
import Surveys from "@/pages/Messaging/Surveys/Surveys";
import Teams from "@/pages/Messaging/Teams/Teams";
import UserProfile, {
  BackToUsersButton
} from "@/pages/Messaging/Users/UserProfile";
import WebMessenger from "@/pages/Messaging/WebMessenger";
import WorkSchedules from "@/pages/Messaging/WorkSchedules/WorkSchedules";
import Users from "@/pages/Messaging/Users/Users";
import AddUserWizard from "@/pages/Messaging/Users/AddUserWizard/AddUserWizard";
import Deployment from "@/pages/Messaging/Deployments/Deployments";
import Schedules from "@/pages/Messaging/Schedules/Schedules";
import AgreementGate from "@/components/AgreementGate";
import BotManager from "@/pages/Messaging/Bots";
import RuleManager from "@/pages/Messaging/Rules";
import RoutingHistory from "@/pages/Messaging/Rules/RoutingHistory";
import WhatsRunning from "@/pages/Messaging/WhatsRunning/WhatsRunning";
import ChatPreview from "@/pages/Messaging/ChatPreview/ChatPreview";
import AuthService from "@/services/AuthService";
import Landing from "@/pages/Messaging/Integrations/IntegrationsLanding";
import FileStorage from "@/pages/Messaging/FileStorage";
import WorkflowsCreate from "@/pages/Messaging/Workflows/WorkflowsCreate";
import Workflows from "@/pages/Messaging/Workflows/Workflows";
import WorkflowsLandingGate from "@/pages/Messaging/Workflows/WorkflowsLandingGate";
import SettingsHub from "@/pages/SettingsHub/SettingsHub";
import ConditionPresetsPage from "@/pages/Settings/ConditionPresets/ConditionPresetsPage";
import FacebookIntegration from "@/pages/Messaging/Channels/FacebookIntegration";
import InstagramIntegration from "@/pages/Messaging/Channels/InstagramIntegration";
import TikTokIntegration from "@/pages/Messaging/Channels/TikTokIntegration";
import TikTokCallback from "@/pages/Messaging/Channels/TikTokCallback";
import SmsIntegration from "@/pages/Messaging/Channels/SmsIntegration";
import WhatsAppIntegration from "@/pages/Messaging/Channels/WhatsAppIntegration";
import RcsIntegration from "@/pages/Messaging/Channels/RcsIntegration";
import ArticlesIndex from "@/kb/articles/Index";
import Skills from "@/pages/Messaging/Skills/Skills";
import ArticlesCreate from "@/kb/articles/Create";
import ArticlesEdit from "@/kb/articles/Edit";
import SettingsIndex from "@/kb/settings/Index";
import TopicsIndex from "@/kb/topics/Index";
import SiteMap from "@/kb/sitemap/Index";
import SetupIndex from "@/kb/setup/Index";
import MessagingSetup from "@/pages/Messaging/Setup/Index";
import ActivityLog from "@/pages/Admin/ActivityLog";
import ToolUsageReport from "@/pages/Admin/ToolUsageReport";
import VelaroLogger from "@/pages/Admin/VelaroLogger";
import AgentLoginLogs from "@/pages/Admin/AgentLoginLogs";
import UserRoles from "@/pages/Messaging/UserRoles";
import CopilotAdmin from "@/pages/Admin/CopilotAdmin";
import PricingQuote from "@/pages/Admin/PricingQuote";
import DatabaseTool from "@/pages/Admin/DatabaseTool";
import SearchIndexes from "@/pages/Admin/SearchIndexes";
import CampaignHealth from "@/pages/Admin/CampaignHealth";
import DetectionReport from "@/pages/Admin/DetectionReport";
import PlatformChannelSetup from "@/pages/Admin/PlatformChannelSetup";
import MFAthleticDemo from "@/pages/Admin/MFAthleticDemo";
import LiveMonitor from "@/pages/Admin/LiveMonitor";
import AccountHealth from "@/pages/Admin/AccountHealth";
import AccountManagerAssignments from "@/pages/Admin/AccountManagerAssignments";
import AIPromptPolicy from "@/pages/Admin/AIPromptPolicy";
import AppMarketplace from "@/pages/Admin/AppMarketplace/AppMarketplace";
import SiteSync from "@/pages/Admin/SiteSync";
import Deployments from "@/pages/Admin/Deployments";
import SubscriptionSettings from "@/pages/Admin/SubscriptionSettings";
import BillingFinancial from "@/pages/Admin/BillingFinancial";
import BillingPlans from "@/pages/Admin/BillingPlans";
import BillingOverage from "@/pages/Admin/BillingOverage";
import KBDomains from "@/pages/Admin/KBDomains";
import HangfireMonitor from "@/pages/Admin/HangfireMonitor";
import SupportTools from "@/pages/Admin/SupportTools";
import AI from "@/pages/Messaging/Bots/AI/AI";
import CreateChatbotButton from "@/pages/Messaging/Bots/AI/CreateChatbotButton";
import NewWorkflowButton from "@/pages/Messaging/Workflows/NewWorkflowButton";
import AIChatbotSettingsGeneral from "@/pages/Messaging/Bots/AI/AIChatbotSettingsGeneral";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import AIChatbotSettingsTraining from "@/pages/Messaging/Bots/AI/AIChatbotSettingsTraining";
import WorkflowsEditor from "@/pages/Messaging/Workflows/WorkflowsEditor";
import VariableGuide from "@/pages/Messaging/Workflows/VariableGuide";
import Formstack from "@/pages/Messaging/Integrations/Formstack/Formstack";
import FormstackCreate from "@/pages/Messaging/Integrations/Formstack/FormstackCreate";
import AIChatbotSettingsHandoff from "@/pages/Messaging/Bots/AI/AIChatbotSettingsHandoff";
import SaveChatbotButton from "@/pages/Messaging/Bots/AI/SaveChatbotButton";
import IvrIntegration from "@/pages/Messaging/Channels/IvrIntegration";
import AddPhoneNumberButton from "@/pages/Messaging/Channels/AddPhoneNumberButton";
import SiteSettings from "@/pages/Messaging/SiteSettings";
import TranscriptSettings from "@/pages/Messaging/SiteSettings/TranscriptSettings";
import AIProviderSettings from "@/pages/Messaging/SiteSettings/AIProviderSettings";
import Tags from "@/pages/Messaging/Tags/Tags";
import Invitations from "@/pages/Messaging/Invitations";
import BaseInvitationEditor from "@/pages/Messaging/Invitations/BaseInvitationEditor";
import AIChatbotSettingsPlayground from "@/pages/Messaging/Bots/AI/AIChatbotSettingsPlayground";
import AIChatbotSettingsTest from "@/pages/Messaging/Bots/AI/AIChatbotSettingsTest";
import AIChatbotSettingsKnowledgeOverrides from "@/pages/Messaging/Bots/AI/AIChatbotSettingsKnowledgeOverrides";
import AIChatbotSettingsSkills from "@/pages/Messaging/Bots/AI/AIChatbotSettingsSkills";
import SearchBar from "@/components/SearchBar";
import CreateIndexButton from "@/pages/Messaging/Bots/AI/CreateIndexButton";
import AIChatbotTrainingIndex from "@/pages/Messaging/Bots/AI/AIChatbotTrainingIndex";
import AIChatbotSettingsTrainingTabFile from "@/pages/Messaging/Bots/AI/AIChatbotFileTrainingIndex";
import AIChatbotFileTraining from "@/pages/Messaging/Bots/AI/AIChatbotFileTraining";
import AIChatbotWebTraining from "@/pages/Messaging/Bots/AI/AIChatbotWebTraining";
import BuildBotWizard from "@/pages/Messaging/Bots/BuildBotWizard";
import LanguageSetupWizard from "@/pages/Messaging/LanguageSetupWizard/LanguageSetupWizard";
import AINewTraining from "@/pages/Messaging/Bots/AINew/AINewTraining";
import AINewChat from "@/pages/Messaging/Bots/AINew/AINewChat";
import AINewSettings from "@/pages/Messaging/Bots/AINew/AINewSettings";
import AINewSettingsGeneral from "@/pages/Messaging/Bots/AINew/AINewSettingsGeneral";
import AINewSettingsHandoff from "@/pages/Messaging/Bots/AINew/AINewSettingsHandoff";
import AINewSettingsPlayground from "@/pages/Messaging/Bots/AINew/AINewSettingsPlayground";
import AINewCreateChatbotButton from "@/pages/Messaging/Bots/AINew/AINewCreateChatbotButton";
import AINewSaveChatbotButton from "@/pages/Messaging/Bots/AINew/AINewSaveChatbotButton";
import AINewIndexes from "@/pages/Messaging/Bots/AINew/AINewIndexes";
import AINewSettingsKnowledgeOverrides from "@/pages/Messaging/Bots/AINew/AINewSettingsKnowledgeOverrides";
import AINewSettingsSkills from "@/pages/Messaging/Bots/AINew/AINewSettingsSkills";
import AINewSettingsChannelOverrides from "@/pages/Messaging/Bots/AINew/AINewSettingsChannelOverrides";
import AINewSettingsQnA from "@/pages/Messaging/Bots/AINew/AINewSettingsQnA";
import { RouteHeader, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import AccelaCivic from "@/pages/Messaging/Integrations/Accela/Accela";
import AccelaCivicCreate from "@/pages/Messaging/Integrations/Accela/AccelaCreate";
import ChatWindow from "@/pages/Messaging/ChatWindow";
import ChatWindowEditor from "@/pages/Messaging/ChatWindow/ChatWindowEditor";
import CreateChatWindowButton from "@/pages/Messaging/ChatWindow/CreateChatWindowButton";
import AccelaCallbackBridge from "@/pages/Messaging/Integrations/Accela/AccelaCallbackBridge";
import CustomFields from "@/pages/ContactCenter/CustomFields";
import MessagingSettings from "@/pages/Messaging/Settings";
import BillingPage from "@/pages/Messaging/Billing";
import Shopify from "@/pages/Messaging/Integrations/Shopify/Shopify";
import ShopifyCreate, {
  ShopifyBackToShopifyOverview
} from "@/pages/Messaging/Integrations/Shopify/ShopifyCreate";
import ShopifyOverview, {
  ShopifyBackToIntegrationLanding
} from "@/pages/Messaging/Integrations/Shopify/ShopifyOverview";
import McpServers from "@/pages/Messaging/Integrations/MCP/McpServers";
import StripePayments from "@/pages/Messaging/Integrations/Stripe";
import HubSpot, {
  BackToIntegrationLandingHubSpot
} from "@/pages/Messaging/Integrations/HubSpot/HubSpot";
import Salesforce, {
  BackToIntegrationLandingSalesforce
} from "@/pages/Messaging/Integrations/Salesforce/Salesforce";
import ServiceNow, {
  BackToIntegrationLandingServiceNow
} from "@/pages/Messaging/Integrations/ServiceNow/ServiceNow";
import ServiceNowCreate from "@/pages/Messaging/Integrations/ServiceNow/ServiceNowCreate";
import SAP from "@/pages/Messaging/Integrations/SAP/SAP";
import SapCreate from "@/pages/Messaging/Integrations/SAP/SapCreate";
import Uplisting, {
  BackToIntegrationLandingUplisting
} from "@/pages/Messaging/Integrations/Uplisting/Uplisting";
import UplistingCreate from "@/pages/Messaging/Integrations/Uplisting/UplistingCreate";
import Guesty, {
  BackToIntegrationLandingGuesty
} from "@/pages/Messaging/Integrations/Guesty/Guesty";
import GuestyCreate from "@/pages/Messaging/Integrations/Guesty/GuestyCreate";
import Cloudbeds, {
  BackToIntegrationLandingCloudbeds
} from "@/pages/Messaging/Integrations/Cloudbeds/Cloudbeds";
import CloudbedsCreate from "@/pages/Messaging/Integrations/Cloudbeds/CloudbedsCreate";
import Pipedrive, {
  BackToIntegrationLandingPipedrive
} from "@/pages/Messaging/Integrations/Pipedrive/Pipedrive";
import QuickBooks, {
  BackToIntegrationLandingQuickBooks
} from "@/pages/Messaging/Integrations/QuickBooks/QuickBooks";
import Zoho, {
  BackToIntegrationLandingZoho
} from "@/pages/Messaging/Integrations/Zoho/Zoho";
import Toast, {
  BackToIntegrationLandingToast
} from "@/pages/Messaging/Integrations/Toast/Toast";
import Clover, {
  BackToIntegrationLandingClover
} from "@/pages/Messaging/Integrations/Clover/index";
import Mindbody, {
  BackToIntegrationLandingMindbody
} from "@/pages/Messaging/Integrations/Mindbody/Mindbody";
import MindbodyCreate from "@/pages/Messaging/Integrations/Mindbody/MindbodyCreate";
import Monday, {
  BackToIntegrationLandingMonday
} from "@/pages/Messaging/Integrations/Monday/Monday";
import Recruiting, {
  BackToIntegrationLandingRecruiting
} from "@/pages/Messaging/Integrations/Recruiting/Recruiting";
import Jobber, {
  BackToIntegrationLandingJobber
} from "@/pages/Messaging/Integrations/Jobber/index";
import MicrosoftBookings, {
  BackToIntegrationLandingMicrosoftBookings
} from "@/pages/Messaging/Integrations/MicrosoftBookings/MicrosoftBookings";
import OpenTableIntegration, {
  BackToIntegrationLandingOpenTable
} from "@/pages/Messaging/Integrations/OpenTable/index";
import ResyIntegration, {
  BackToIntegrationLandingResy
} from "@/pages/Messaging/Integrations/Resy/index";
import HubSpotCreate from "@/pages/Messaging/Integrations/HubSpot/HubSpotCreate";
import HubSpotOverview, {
  HubSpotBackToIntegrationLanding,
  HubSpotBackToHubSpotOverview
} from "@/pages/Messaging/Integrations/HubSpot/HubSpotOverview";
import BigCommerce, {
  BackToIntegrationLandingBigCommerce
} from "@/pages/Messaging/Integrations/BigCommerce/BigCommerce";
import WooCommerce, {
  BackToIntegrationLandingWooCommerce
} from "@/pages/Messaging/Integrations/WooCommerce/WooCommerce";
import BigCommerceCreate from "@/pages/Messaging/Integrations/BigCommerce/BigCommerceCreate";
import BigCommerceOverview, {
  BigCommerceBackToIntegrationLanding,
  BigCommerceBackToOverview
} from "@/pages/Messaging/Integrations/BigCommerce/BigCommerceOverview";
import Magento, {
  BackToIntegrationLandingMagento
} from "@/pages/Messaging/Integrations/Magento/Magento";
import MagentoCreate from "@/pages/Messaging/Integrations/Magento/MagentoCreate";
import MagentoOverview, {
  MagentoBackToIntegrationLanding,
  MagentoBackToOverview
} from "@/pages/Messaging/Integrations/Magento/MagentoOverview";
import Dynamics, {
  BackToIntegrationLandingDynamics
} from "@/pages/Messaging/Integrations/Dynamics/Dynamics";
import DynamicsCreate from "@/pages/Messaging/Integrations/Dynamics/DynamicsCreate";
import DynamicsOverview, {
  DynamicsBackToIntegrationLanding,
  DynamicsBackToOverview
} from "@/pages/Messaging/Integrations/Dynamics/DynamicsOverview";
import PageWidgets from "@/pages/Messaging/PageWidgets";
import ProactiveEngagement from "@/pages/Messaging/ProactiveEngagement";
import VisitorMonitor from "@/pages/Messaging/VisitorMonitor";
import EmailCampaigns from "@/pages/Messaging/EmailCampaigns";
import IvrCampaigns from "@/pages/Messaging/IvrCampaigns";
import PowerAutomate from "@/pages/Messaging/Integrations/PowerAutomate";
import MakeIntegration from "@/pages/Messaging/Integrations/Make";
import ZapierIntegration from "@/pages/Messaging/Integrations/Zapier";
import TranscriptStorage from "@/pages/Messaging/Integrations/TranscriptStorage";
import Microsoft365 from "@/pages/Messaging/Integrations/Microsoft365";
import TeamsNotifications from "@/pages/Messaging/Integrations/Microsoft365/TeamsNotifications";
import OneDriveSettingsPage from "@/pages/Messaging/Integrations/Microsoft365/OneDriveSettings";
import SlackNotificationsPage from "@/pages/Messaging/Integrations/SlackNotifications";
import AlertRulesPage from "@/pages/Messaging/Integrations/AlertRules";
import GmailInbox from "@/pages/Messaging/Integrations/GmailInbox";
import OutlookInbox from "@/pages/Messaging/Integrations/OutlookInbox";
import OutlookCalendar from "@/pages/Messaging/Integrations/OutlookCalendar";
import CalendlyIntegration from "@/pages/Messaging/Integrations/Calendly";
import SquareIntegration from "@/pages/Messaging/Integrations/Square";
import SquareCallbackPage from "@/pages/Messaging/Integrations/Square/Callback";
import KlaviyoIntegration, {
  BackToIntegrationLandingKlaviyo
} from "@/pages/Messaging/Integrations/Klaviyo/Klaviyo";
import Vast, {
  BackToIntegrationLandingVast
} from "@/pages/Messaging/Integrations/Vast/Vast";
import NextivaPage, {
  BackToIntegrationLandingNextiva
} from "@/pages/Messaging/Integrations/Nextiva/Nextiva";
import SecureFormsPanel, {
  BackToIntegrationLandingSecureForms
} from "@/pages/Messaging/Integrations/SecureForms/SecureFormsPanel";
import GorgiasWizard, {
  BackToIntegrationLandingGorgias
} from "@/pages/Messaging/Integrations/Gorgias/GorgiasWizard";
import GoogleTagManager, {
  BackToIntegrationLandingGTM
} from "@/pages/Messaging/Integrations/GoogleTagManager/GoogleTagManager";
import NativeCrm, {
  BackToIntegrationLandingNativeCrm
} from "@/pages/Messaging/Integrations/NativeCrm/NativeCrm";
import Packages from "@/pages/Messaging/Packages";
import ContactSupport from "@/pages/Support/ContactSupport";
import Dashboard from "@/pages/Dashboard";
import ClickToCallPage from "@/pages/Messaging/ClickToCall";
import AvatarSettings from "@/pages/Messaging/Avatar/AvatarSettings";
import ValueReport from "@/pages/Reports/ValueReport";
import AgentPerformance from "@/pages/Reports/AgentPerformance";
import Leaderboard from "@/pages/Reports/Leaderboard";
import ChatIntelligence from "@/pages/Reports/ChatIntelligence";
import CsatReport from "@/pages/Reports/CsatReport";
import ConversionReport from "@/pages/Reports/ConversionReport";
import Conversions from "@/pages/Messaging/Settings/Conversions";
import Contracts from "@/pages/Billing/Contracts";
import ContractTemplates from "@/pages/Billing/ContractTemplates";
import SigningPage from "@/pages/Billing/SigningPage";
import BillingInvoices from "@/pages/Billing/Invoices";
import BillingTemplates from "@/pages/Billing/InvoiceTemplates";
import BillingStripeSettings from "@/pages/Billing/StripeSettings";
import QuoteAcceptPage from "@/pages/Billing/QuoteAcceptPage";
import NotFoundPage from "@/pages/Shared/NotFoundPage";

const handleSearch = (searchText: string) => {};

// Redirect helper — reads :id from the route and sends to the equivalent /ai-new/ tab
function RedirectAIToNew({ suffix }: { suffix: string }) {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/ai-new/settings/${suffix}/${id ?? ""}`} replace />;
}

const handleAccountSearch = (searchText: string) => {
  window.dispatchEvent(
    new CustomEvent("accountSearch", { detail: searchText })
  );
};

const createAppRoutes = (mainLoader: (params: any) => any) => [
  { path: "auth/callback", element: <EntraCallback /> },
  { path: "login/callback", element: <LoginCallback /> },
  { path: "login/callback/error", element: <LoginCallbackError /> },
  { path: "sign/:token", element: <SigningPage /> },
  { path: "quote/:token", element: <QuoteAcceptPage /> },
  { path: "login/unauthorized", element: <LoginUnauthorized /> },
  { path: "logout", element: <Logout /> },
  { path: "logout/callback", element: <LogoutCallback /> },
  {
    element: <PrivateRoute />,
    children: [
      { path: "/", element: <Home /> },
      {
        path: "/dashboard",
        element: (
          <RouteHeader title="Live Operations Dashboard">
            <Dashboard />
          </RouteHeader>
        )
      },
      {
        path: "/account",
        element: <SiteSettings />
      },
      {
        path: "/account/transcript-settings",
        element: (
          <RouteHeader title="Transcript Settings">
            <TranscriptSettings />
          </RouteHeader>
        )
      },
      {
        path: "/admin/database-tool",
        element: (
          <RouteHeader title="Database Administration">
            <DatabaseTool />
          </RouteHeader>
        )
      },
      {
        path: "/admin/hangfire",
        element: (
          <RouteHeader title="Hangfire Monitor">
            <HangfireMonitor />
          </RouteHeader>
        )
      },
      {
        path: "/admin/search-indexes",
        element: (
          <RouteHeader title="Search Index Management">
            <SearchIndexes />
          </RouteHeader>
        )
      },
      {
        path: "/admin/site-sync",
        element: (
          <RouteHeader title="Site Sync">
            <SiteSync />
          </RouteHeader>
        )
      },
      {
        path: "/admin/deployments",
        element: (
          <RouteHeader title="Deployments">
            <Deployments />
          </RouteHeader>
        )
      },
      {
        path: "/admin/subscription-settings",
        element: (
          <RouteHeader title="Subscription Settings">
            <SubscriptionSettings />
          </RouteHeader>
        )
      },
      {
        path: "/admin/kb-domains",
        element: (
          <RouteHeader title="KB Custom Domains">
            <KBDomains />
          </RouteHeader>
        )
      },
      {
        path: "/admin/app-marketplace",
        element: (
          <RouteHeader title="App Marketplace Credentials">
            <AppMarketplace />
          </RouteHeader>
        )
      },
      {
        path: "/admin/campaign-health",
        element: (
          <RouteHeader title="Campaign Health">
            <CampaignHealth />
          </RouteHeader>
        )
      },
      {
        path: "/admin/detection-report",
        element: (
          <RouteHeader title="Site Detection Report">
            <DetectionReport />
          </RouteHeader>
        )
      },
      {
        path: "/admin/copilot",
        element: (
          <RouteHeader title="AI Platform">
            <CopilotAdmin />
          </RouteHeader>
        )
      },
      {
        path: "/admin/pricing-quote",
        element: (
          <RouteHeader title="Pricing & Quote Builder">
            <PricingQuote />
          </RouteHeader>
        )
      },
      {
        path: "/admin/platform-setup",
        element: (
          <RouteHeader title="Platform Channel Setup">
            <PlatformChannelSetup />
          </RouteHeader>
        )
      },
      {
        path: "/admin/mf-athletic-demo",
        element: (
          <RouteHeader title="MF Athletic Demo">
            <MFAthleticDemo />
          </RouteHeader>
        )
      },
      {
        path: "/admin/ai-prompt-policy",
        element: (
          <RouteHeader title="AI Prompt Policies">
            <AIPromptPolicy />
          </RouteHeader>
        )
      },
      {
        path: "/admin/live-monitor",
        element: <LiveMonitor />
      },
      {
        path: "/admin/account-health",
        element: <AccountHealth />
      },
      {
        path: "/admin/am-assignments",
        element: <AccountManagerAssignments />
      },
      {
        path: "/admin/billing-overage",
        element: (
          <RouteHeader title="Billing Overage Settings">
            <BillingOverage />
          </RouteHeader>
        )
      },
      {
        path: "/admin/billing-financial",
        element: (
          <RouteHeader title="Financial Dashboard">
            <BillingFinancial />
          </RouteHeader>
        )
      },
      {
        path: "/admin/billing/plans",
        element: (
          <RouteHeader title="Plan Management">
            <BillingPlans />
          </RouteHeader>
        )
      },
      {
        path: "/admin/activity-log",
        element: (
          <RouteHeader title="Activity Log">
            <ActivityLog />
          </RouteHeader>
        )
      },
      {
        path: "/admin/velaro-logger",
        element: (
          <RouteHeader title="Velaro Logger">
            <VelaroLogger />
          </RouteHeader>
        )
      },
      {
        path: "/admin/agent-login-logs",
        element: (
          <RouteHeader title="Agent Login History">
            <AgentLoginLogs />
          </RouteHeader>
        )
      },
      {
        path: "/admin/support-tools",
        element: (
          <RouteHeader title="Support Tools">
            <SupportTools />
          </RouteHeader>
        )
      },
      {
        path: "/admin/tool-usage",
        element: (
          <RouteHeader title="Tool Usage Report">
            <ToolUsageReport />
          </RouteHeader>
        )
      },
      {
        path: "/settings/roles",
        element: (
          <RouteHeader title="Roles & Permissions">
            <UserRoles />
          </RouteHeader>
        )
      },
      {
        path: "/settings/ai-provider",
        element: (
          <RouteHeader title="AI Provider">
            <AIProviderSettings />
          </RouteHeader>
        )
      },
      {
        path: "/settings/hub",
        element: (
          <RouteHeader title="Settings">
            <SettingsHub />
          </RouteHeader>
        )
      },
      {
        path: "/settings/condition-presets",
        element: (
          <RouteHeader title="Condition Presets">
            <ConditionPresetsPage />
          </RouteHeader>
        )
      },
      {
        path: "/users/",
        element: <Users />
      },
      {
        path: "/users/:userId",
        element: (
          <RouteHeader
            title="User Profile"
            headerActions={<BackToUsersButton />}
          >
            <UserProfile />
          </RouteHeader>
        )
      },
      {
        path: "/user/add",
        element: (
          <RouteHeader title="Add User">
            <AddUserWizard />
          </RouteHeader>
        )
      },
      {
        path: "/contact_center/*",
        element: (
          <RouteHeader title="Contact Center">
            <ContactCenter />
          </RouteHeader>
        )
      },
      {
        path: "/teams",
        element: <Teams />
      },
      {
        path: "/schedules",
        element: <Schedules />
      },
      {
        path: "/deployment",
        element: (
          <StickyRouteHeader
            title="Deployment"
          >
            <AgreementGate slug="deployment-compliance">
              <Deployment />
            </AgreementGate>
          </StickyRouteHeader>
        )
      },
      {
        path: "/tags",
        element: (
          <RouteHeader title="Tags">
            <Tags />
          </RouteHeader>
        )
      },
      {
        path: "/custom-fields",
        element: (
          <RouteHeader title="Custom Fields">
            <CustomFields />
          </RouteHeader>
        )
      },
      {
        path: "/facebook",
        element: (
          <RouteHeader title="Facebook">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <FacebookIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/instagram",
        element: (
          <RouteHeader title="Instagram">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <InstagramIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/tiktok",
        element: (
          <RouteHeader title="TikTok">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <TikTokIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/tiktok/callback",
        element: <TikTokCallback />
      },
      {
        path: "/sms",
        element: (
          <RouteHeader title="SMS">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <SmsIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/whatsapp",
        element: (
          <RouteHeader title="WhatsApp">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <WhatsAppIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/rcs",
        element: (
          <RouteHeader title="RCS Business Messaging">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 overflow-hidden min-w-0">
              <RcsIntegration />
            </div>
          </RouteHeader>
        )
      },
      {
        path: "/ivr",
        element: (
          <RouteHeader title="IVR" headerActions={<AddPhoneNumberButton />}>
            <IvrIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/click-to-call",
        element: (
          <RouteHeader title="Click-to-Call">
            <ClickToCallPage />
          </RouteHeader>
        )
      },
      {
        path: "/avatar",
        element: (
          <RouteHeader title="AI Video Avatars">
            <AvatarSettings />
          </RouteHeader>
        )
      },
      {
        path: "/surveys",
        element: (
          <RouteHeader title="Surveys">
            <Surveys />
          </RouteHeader>
        )
      },
      {
        path: "/integrations",
        element: (
          <StickyRouteHeader
            title="Integrations"
            subTitle="Configure integrations to fit your specific business needs, enhancing your ability to manage everything in one place."
            headerActions={
              <SearchBar onSearch={handleSearch} placeholderText="Search" />
            }
          >
            <Landing />
          </StickyRouteHeader>
        )
      },
      {
        path: "/twitter/callback",
        element: <TwitterCallback />
      },
      {
        path: "/netsuite/oauthcallback",
        element: <NetSuiteCallback />
      },
      {
        path: "/auto_responses",
        element: (
          <RouteHeader title="Auto Responses">
            <AutoResponses />
          </RouteHeader>
        )
      },
      {
        path: "/canned_responses",
        element: <CannedResponses />
      },
      {
        path: "/web_messenger",
        element: (
          <RouteHeader title="Web Messenger">
            <WebMessenger />
          </RouteHeader>
        )
      },
      {
        path: "/chat_window",
        element: (
          <RouteHeader
            title="Chat Window"
            headerActions={<CreateChatWindowButton />}
          >
            <ChatWindow />
          </RouteHeader>
        )
      },
      {
        path: "/chat_window/:chatWindowId",
        element: <ChatWindowEditor />
      },
      {
        path: "/work_schedules",
        element: (
          <RouteHeader title="Work Schedules">
            <WorkSchedules />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/netsuite",
        element: (
          <RouteHeader
            title="Netsuite CRM"
            headerActions={<BackToIntegrationLanding />}
          >
            <GatedSubscriptionFeature flagKey="enableNetsuite" name="NetSuite CRM">
              <NetSuite />
            </GatedSubscriptionFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/netsuite/netsuitecreate",
        element: (
          <RouteHeader title="">
            <NetSuiteCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/formstack",
        element: (
          <RouteHeader title="Formstack" headerActions={<BackToIntegrations />}>
            <GatedSubscriptionFeature flagKey="enableFormstack" name="Formstack">
              <Formstack />
            </GatedSubscriptionFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/formstack/formstackcreate",
        element: (
          <RouteHeader title="">
            <FormstackCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/accela-civic",
        element: (
          <RouteHeader title="Accela" headerActions={<BackToIntegrations />}>
            <GatedSubscriptionFeature flagKey="enableAccela" name="Accela Civic">
              <AccelaCivic />
            </GatedSubscriptionFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/accela-civic/AccelaCreate",
        element: (
          <RouteHeader title="Accela">
            <AccelaCivicCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/accela-civic/AccelaCallbackBridge",
        element: (
          <RouteHeader title="Accela">
            <AccelaCallbackBridge />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/Shopify",
        element: (
          <RouteHeader title="Shopify" headerActions={<BackToIntegrations />}>
            <GatedAdminFeature flagKey="enableShopify" name="Shopify">
              <Shopify />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/shopify/ShopifyCreate",
        element: (
          <RouteHeader
            title=""
            headerActions={<ShopifyBackToShopifyOverview />}
          >
            <ShopifyCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/shopify/ShopifyOverview",
        element: (
          <RouteHeader
            title=""
            headerActions={<ShopifyBackToIntegrationLanding />}
          >
            <ShopifyOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/log",
        element: (
          <RouteHeader title="Integration Log" headerActions={<BackToIntegrations />}>
            <IntegrationLog />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/mcp",
        element: (
          <RouteHeader title="MCP Servers" headerActions={<BackToIntegrations />}>
            <McpServers />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/payments/stripe",
        element: (
          <RouteHeader title="Stripe Payments" headerActions={<BackToIntegrations />}>
            <StripePayments />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/hubspot",
        element: (
          <RouteHeader title="HubSpot CRM" headerActions={<BackToIntegrationLandingHubSpot />}>
            <GatedAdminFeature flagKey="enableHubSpot" name="HubSpot CRM">
              <HubSpot />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/hubspot/overview",
        element: (
          <RouteHeader title="" headerActions={<HubSpotBackToIntegrationLanding />}>
            <HubSpotOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/hubspot/create",
        element: (
          <RouteHeader title="" headerActions={<HubSpotBackToHubSpotOverview />}>
            <HubSpotCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/bigcommerce",
        element: (
          <RouteHeader title="BigCommerce" headerActions={<BackToIntegrationLandingBigCommerce />}>
            <GatedAdminFeature flagKey="enableBigCommerce" name="BigCommerce">
              <BigCommerce />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/bigcommerce/overview",
        element: (
          <RouteHeader title="" headerActions={<BigCommerceBackToIntegrationLanding />}>
            <BigCommerceOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/bigcommerce/create",
        element: (
          <RouteHeader title="" headerActions={<BigCommerceBackToOverview />}>
            <BigCommerceCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/magento",
        element: (
          <RouteHeader title="Magento / Adobe Commerce" headerActions={<BackToIntegrationLandingMagento />}>
            <GatedAdminFeature flagKey="enableMagento" name="Magento / Adobe Commerce">
              <Magento />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/magento/overview",
        element: (
          <RouteHeader title="" headerActions={<MagentoBackToIntegrationLanding />}>
            <MagentoOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/magento/create",
        element: (
          <RouteHeader title="" headerActions={<MagentoBackToOverview />}>
            <MagentoCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/woocommerce",
        element: (
          <RouteHeader title="WooCommerce" headerActions={<BackToIntegrationLandingWooCommerce />}>
            <GatedAdminFeature flagKey="enableWooCommerce" name="WooCommerce">
              <WooCommerce />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/dynamics",
        element: (
          <RouteHeader title="Microsoft Dynamics 365" headerActions={<BackToIntegrationLandingDynamics />}>
            <GatedAdminFeature flagKey="enableDynamics" name="Microsoft Dynamics 365">
              <Dynamics />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/dynamics/overview",
        element: (
          <RouteHeader title="" headerActions={<DynamicsBackToIntegrationLanding />}>
            <DynamicsOverview />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/dynamics/create",
        element: (
          <RouteHeader title="" headerActions={<DynamicsBackToOverview />}>
            <DynamicsCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/velaro-crm",
        element: (
          <RouteHeader title="Velaro CRM" headerActions={<BackToIntegrationLandingNativeCrm />}>
            <NativeCrm />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/salesforce",
        element: (
          <RouteHeader title="Salesforce CRM" headerActions={<BackToIntegrationLandingSalesforce />}>
            <GatedAdminFeature flagKey="enableSalesforce" name="Salesforce CRM">
              <Salesforce />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/servicenow",
        element: (
          <RouteHeader title="ServiceNow ITSM" headerActions={<BackToIntegrationLandingServiceNow />}>
            <ServiceNow />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/servicenow/create",
        element: (
          <RouteHeader title="Connect ServiceNow" headerActions={<BackToIntegrationLandingServiceNow />}>
            <ServiceNowCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/sap",
        element: (
          <RouteHeader title="SAP Business One" headerActions={<BackToIntegrations />}>
            <SAP />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/sap/create",
        element: (
          <RouteHeader title="Connect SAP" headerActions={<BackToIntegrations />}>
            <SapCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/uplisting",
        element: (
          <RouteHeader title="Uplisting" headerActions={<BackToIntegrationLandingUplisting />}>
            <GatedAdminFeature flagKey="enableUplisting" name="Uplisting">
              <Uplisting />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/uplisting/create",
        element: (
          <RouteHeader title="Connect Uplisting" headerActions={<BackToIntegrationLandingUplisting />}>
            <UplistingCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/guesty",
        element: (
          <RouteHeader title="Guesty" headerActions={<BackToIntegrationLandingGuesty />}>
            <GatedAdminFeature flagKey="enableGuesty" name="Guesty">
              <Guesty />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/guesty/create",
        element: (
          <RouteHeader title="Connect Guesty" headerActions={<BackToIntegrationLandingGuesty />}>
            <GuestyCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/cloudbeds",
        element: (
          <RouteHeader title="Cloudbeds" headerActions={<BackToIntegrationLandingCloudbeds />}>
            <GatedAdminFeature flagKey="enableCloudbeds" name="Cloudbeds">
              <Cloudbeds />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/cloudbeds/create",
        element: (
          <RouteHeader title="Connect Cloudbeds" headerActions={<BackToIntegrationLandingCloudbeds />}>
            <CloudbedsCreate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/pipedrive",
        element: (
          <RouteHeader title="Pipedrive CRM" headerActions={<BackToIntegrationLandingPipedrive />}>
            <GatedAdminFeature flagKey="enablePipedrive" name="Pipedrive">
              <Pipedrive />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/quickbooks",
        element: (
          <RouteHeader title="QuickBooks Online" headerActions={<BackToIntegrationLandingQuickBooks />}>
            <GatedAdminFeature flagKey="enableQuickBooks" name="QuickBooks Online">
              <QuickBooks />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/zoho",
        element: (
          <RouteHeader title="Zoho CRM" headerActions={<BackToIntegrationLandingZoho />}>
            <GatedAdminFeature flagKey="enableZoho" name="Zoho CRM">
              <Zoho />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/toast",
        element: (
          <RouteHeader title="Toast POS" headerActions={<BackToIntegrationLandingToast />}>
            <GatedAdminFeature flagKey="enableToast" name="Toast POS">
              <Toast />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/clover",
        element: (
          <RouteHeader title="Clover POS" headerActions={<BackToIntegrationLandingClover />}>
            <GatedSubscriptionFeature flagKey="enableCloverSkills" name="Clover POS">
              <Clover />
            </GatedSubscriptionFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/mindbody",
        element: (
          <RouteHeader title="Mindbody" headerActions={<BackToIntegrationLandingMindbody />}>
            <GatedAdminFeature flagKey="enableMindbody" name="Mindbody">
              <Mindbody />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/mindbody/create",
        element: (
          <RouteHeader title="Connect Mindbody" headerActions={<BackToIntegrationLandingMindbody />}>
            <GatedAdminFeature flagKey="enableMindbody" name="Mindbody">
              <MindbodyCreate />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/monday",
        element: (
          <RouteHeader title="Monday.com" headerActions={<BackToIntegrationLandingMonday />}>
            <GatedAdminFeature flagKey="enableMonday" name="Monday.com">
              <Monday />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/recruiting",
        element: (
          <RouteHeader title="Recruiting Bot" headerActions={<BackToIntegrationLandingRecruiting />}>
            <GatedSubscriptionFeature flagKey="enableRecruitingBot" name="Recruiting Bot">
              <Recruiting />
            </GatedSubscriptionFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/jobber",
        element: (
          <RouteHeader title="Jobber" headerActions={<BackToIntegrationLandingJobber />}>
            <GatedAdminFeature flagKey="enableJobber" name="Jobber">
              <Jobber />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/power-automate",
        element: (
          <RouteHeader title="Power Automate" headerActions={<BackToIntegrations />}>
            <PowerAutomate />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/make",
        element: (
          <RouteHeader title="Make (Integromat)" headerActions={<BackToIntegrations />}>
            <MakeIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/zapier",
        element: (
          <RouteHeader title="Zapier" headerActions={<BackToIntegrations />}>
            <ZapierIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/transcript-storage",
        element: (
          <RouteHeader title="Transcript Storage" headerActions={<BackToIntegrations />}>
            <TranscriptStorage />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/microsoft-365",
        element: (
          <RouteHeader title="Microsoft 365" headerActions={<BackToIntegrations />}>
            <Microsoft365 />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/microsoft-bookings",
        element: (
          <RouteHeader title="Microsoft Bookings" headerActions={<BackToIntegrationLandingMicrosoftBookings />}>
            <GatedSubscriptionFeature flagKey="enableMicrosoftBookingsSkills" name="Microsoft Bookings">
              <MicrosoftBookings />
            </GatedSubscriptionFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/opentable",
        element: (
          <RouteHeader title="OpenTable" headerActions={<BackToIntegrationLandingOpenTable />}>
            <OpenTableIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/resy",
        element: (
          <RouteHeader title="Resy" headerActions={<BackToIntegrationLandingResy />}>
            <ResyIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/microsoft-365/teams-notifications",
        element: (
          <RouteHeader title="Teams Notifications" headerActions={<BackToIntegrations backTo="/integrations/microsoft-365" />}>
            <TeamsNotifications />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/microsoft-365/onedrive",
        element: (
          <RouteHeader title="OneDrive File Attachments" headerActions={<BackToIntegrations backTo="/integrations/microsoft-365" />}>
            <OneDriveSettingsPage />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/teams-notifications",
        element: (
          <RouteHeader title="Teams Notifications" headerActions={<BackToIntegrations />}>
            <TeamsNotifications />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/slack-notifications",
        element: (
          <RouteHeader title="Slack Notifications" headerActions={<BackToIntegrations />}>
            <SlackNotificationsPage />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/alert-rules",
        element: (
          <RouteHeader title="Alert Rules" headerActions={<BackToIntegrations />}>
            <AlertRulesPage />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/gmail-inbox",
        element: (
          <RouteHeader title="Gmail Inbox" headerActions={<BackToIntegrations />}>
            <GmailInbox />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/outlook-inbox",
        element: (
          <RouteHeader title="Outlook Inbox" headerActions={<BackToIntegrations />}>
            <OutlookInbox />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/outlook-calendar",
        element: (
          <RouteHeader title="Outlook Calendar Booking" headerActions={<BackToIntegrations />}>
            <OutlookCalendar />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/calendly",
        element: (
          <RouteHeader title="Calendly" headerActions={<BackToIntegrations />}>
            <CalendlyIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/square-appointments",
        element: (
          <RouteHeader title="Square Appointments" headerActions={<BackToIntegrations />}>
            <SquareIntegration />
          </RouteHeader>
        )
      },
      {
        // OAuth2 callback landing page — Square redirects here with ?code=...&state=...
        path: "/integrations/square/callback",
        element: <SquareCallbackPage />
      },
      {
        path: "/integrations/klaviyo",
        element: (
          <RouteHeader title="Klaviyo" headerActions={<BackToIntegrationLandingKlaviyo />}>
            <KlaviyoIntegration />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/vast",
        element: (
          <RouteHeader title="VAST Retail" headerActions={<BackToIntegrationLandingVast />}>
            <GatedAdminFeature flagKey="enableVast" name="VAST Retail">
              <Vast />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/nextiva",
        element: (
          <RouteHeader title="Nextiva" headerActions={<BackToIntegrationLandingNextiva />}>
            <GatedAdminFeature flagKey="enableNextiva" name="Nextiva">
              <NextivaPage />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/secure-forms",
        element: (
          <RouteHeader title="SecureForms" headerActions={<BackToIntegrationLandingSecureForms />}>
            <GatedAdminFeature flagKey="enableSecureForms" name="SecureForms">
              <SecureFormsPanel />
            </GatedAdminFeature>
          </RouteHeader>
        )
      },
      {
        path: "/integrations/gorgias",
        element: (
          <RouteHeader title="Migrate from Gorgias" headerActions={<BackToIntegrationLandingGorgias />}>
            <GorgiasWizard />
          </RouteHeader>
        )
      },
      {
        path: "/integrations/google-tag-manager",
        element: (
          <RouteHeader title="Google Tag Manager" headerActions={<BackToIntegrationLandingGTM />}>
            <GoogleTagManager />
          </RouteHeader>
        )
      },
      {
        // "Build Your Bot" 5-step wizard — new entry point, does not touch existing /bots routes
        path: "/chatbots/new",
        element: <BuildBotWizard />
      },
      {
        // Language & Translation setup wizard — additive, does not override existing config
        path: "/language-setup",
        element: <LanguageSetupWizard />
      },
      {
        path: "/bots",
        element: (
          <RouteHeader title="Bot Personas">
            <BotManager />
          </RouteHeader>
        )
      },
      {
        path: "/bots/training/index",
        element: (
          <StickyRouteHeader
            title="AI Training"
            headerActions={<CreateIndexButton></CreateIndexButton>}
          >
            <AIChatbotTrainingIndex />
          </StickyRouteHeader>
        )
      },
      {
        path: "/bots/training/web",
        element: (
          <StickyRouteHeader title="AI Training" headerActions={null}>
            <AIChatbotWebTraining />
          </StickyRouteHeader>
        )
      },
      {
        path: "/bots/training/file",
        element: (
          <StickyRouteHeader title="AI Training" headerActions={null}>
            <AIChatbotFileTraining />
          </StickyRouteHeader>
        )
      },
      // /ai/* → redirect to /ai-new/* (all old chatbot URLs forward to the new UI)
      { path: "/ai", element: <Navigate to="/ai-new/settings" replace /> },
      { path: "/ai/settings/general/:id", element: <RedirectAIToNew suffix="general" /> },
      { path: "/ai/settings/training/:id", element: <RedirectAIToNew suffix="general" /> },
      { path: "/ai/settings/handoff/:id", element: <RedirectAIToNew suffix="handoff" /> },
      { path: "/ai/settings/playground/:id", element: <RedirectAIToNew suffix="test-chat" /> },
      { path: "/ai/settings/knowledge-overrides/:id", element: <RedirectAIToNew suffix="knowledge-overrides" /> },
      { path: "/ai/settings/qna/:id", element: <RedirectAIToNew suffix="qna" /> },
      { path: "/ai/settings/skills/:id", element: <RedirectAIToNew suffix="skills" /> },
      { path: "/ai/settings/test/:id", element: <RedirectAIToNew suffix="test-chat" /> },
      // {
      //   path: "/ai/settings/data/:id",
      //   element: (
      //     <StickyRouteHeader
      //       title="AI Chatbot"
      //       headerActions={<SaveChatbotButton></SaveChatbotButton>}
      //       backLink="/ai"
      //     >
      //       <AIChatbotSettingsData />
      //     </StickyRouteHeader>
      //   )
      // },
      // {
      //   path: "/ai/settings/questions/:id",
      //   element: (
      //     <StickyRouteHeader
      //       title="AI Chatbot"
      //       headerActions={<SaveChatbotButton></SaveChatbotButton>}
      //       backLink="/ai"
      //     >
      //       <AIChatbotSettingsStarterQuestions />
      //     </StickyRouteHeader>
      //   )
      // },
      {
        path: "/ai-new/settings",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            headerActions={<AINewCreateChatbotButton />}
          >
            <AINewSettings />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/general/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsGeneral />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/handoff/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsHandoff />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/test-chat/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsPlayground />
          </StickyRouteHeader>
        )
      },
      // Legacy playground path → redirect to test-chat
      { path: "/ai-new/settings/playground/:id", element: <RedirectAIToNew suffix="test-chat" /> },
      {
        path: "/ai-new/settings/knowledge-overrides/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsKnowledgeOverrides />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/skills/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            backLink="/ai-new/settings"
          >
            <AINewSettingsSkills />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/channel-overrides/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsChannelOverrides />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/settings/qna/:id",
        element: (
          <StickyRouteHeader
            title="Chatbot Configuration"
            headerActions={<AINewSaveChatbotButton />}
            backLink="/ai-new/settings"
          >
            <AINewSettingsQnA />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/training",
        element: (
          <StickyRouteHeader title="Chatbot Configuration">
            <AINewTraining />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/chat",
        element: (
          <StickyRouteHeader title="Chatbot Configuration">
            <AINewChat />
          </StickyRouteHeader>
        )
      },
      {
        path: "/ai-new/indexes",
        element: (
          <StickyRouteHeader title="Knowledge Indexes">
            <AINewIndexes />
          </StickyRouteHeader>
        )
      },
      {
        path: "/rules",
        element: (
          <RouteHeader title="Rules">
            <RuleManager />
          </RouteHeader>
        )
      },
      {
        path: "/routing-history",
        element: (
          <RouteHeader title="Routing History">
            <RoutingHistory />
          </RouteHeader>
        )
      },
      {
        path: "/whats-running",
        element: (
          <RouteHeader title="What's Running">
            <WhatsRunning />
          </RouteHeader>
        )
      },
      {
        path: "/chat-preview",
        element: (
          <RouteHeader title="Preview Chat">
            <ChatPreview />
          </RouteHeader>
        )
      },
      {
        path: "/files",
        element: (
          <RouteHeader title="File Upload">
            <FileStorage />
          </RouteHeader>
        )
      },
      {
        path: "/workflows/create",
        element: (
          <RouteHeader title="Create New Workflow">
            <hr className="pb-6" />
            <WorkflowsCreate />
          </RouteHeader>
        )
      },
      {
        path: "/workflows",
        element: (
          <StickyRouteHeader
            title="Workflows"
            headerActions={<NewWorkflowButton />}
          >
            <WorkflowsLandingGate />
          </StickyRouteHeader>
        )
      },
      {
        path: "/workflows/variable-guide",
        element: (
          <RouteHeader title="Variable Guide">
            <VariableGuide />
          </RouteHeader>
        )
      },
      {
        path: "/workflows/:id",
        element: <WorkflowsEditor />
      },
      {
        path: "/invitations",
        element: (
          <RouteHeader title="Invitations">
            <Invitations />
          </RouteHeader>
        )
      },
      {
        path: "/skills",
        element: (
          <RouteHeader title="Skills">
            <Skills />
          </RouteHeader>
        )
      },

      {
        path: "/kb/articles",
        element: (
          <RouteHeader title="KB Articles">
            <ArticlesIndex />
          </RouteHeader>
        )
      },
      {
        path: "/kb/settings",
        element: (
          <RouteHeader title="KB Settings">
            <SettingsIndex />
          </RouteHeader>
        )
      },
      {
        path: "/kb/articles/create",
        element: (
          <RouteHeader title="Create KB Article">
            <ArticlesCreate />
          </RouteHeader>
        )
      },
      //todo this id needs fixed
      {
        path: "/kb/articles/edit/:id",
        element: (
          <RouteHeader title="Edit KB Article">
            <ArticlesEdit />
          </RouteHeader>
        )
      },
      {
        path: "/kb/topics",
        element: (
          <RouteHeader title="KB Topics">
            <TopicsIndex />
          </RouteHeader>
        )
      },
      {
        path: "/kb/map",
        element: (
          <RouteHeader title="KB Site Map">
            <SiteMap />
          </RouteHeader>
        )
      },
      {
        path: "/kb/setup",
        element: (
          <RouteHeader title="KB Setup">
            <SetupIndex />
          </RouteHeader>
        )
      },
      {
        path: "/messaging/setup",
        element: <MessagingSetup />
      },
      {
        path: "/settings",
        element: (
          <RouteHeader title="Settings">
            <MessagingSettings />
          </RouteHeader>
        )
      },
      // Friendly deep-link aliases used by the copilot — redirect to /settings?tab=...
      {
        path: "/settings/developer",
        element: <Navigate to="/settings?tab=apiKeys" replace />
      },
      {
        path: "/settings/security",
        element: <Navigate to="/settings?tab=security" replace />
      },
      {
        path: "/billing",
        element: <BillingPage />
      },
      {
        path: "/billing/contracts",
        element: (
          <RouteHeader title="Contracts">
            <Contracts />
          </RouteHeader>
        )
      },
      {
        path: "/billing/contracts/templates",
        element: (
          <RouteHeader title="Contract Templates">
            <ContractTemplates />
          </RouteHeader>
        )
      },
      {
        path: "/billing/invoices",
        element: (
          <RouteHeader title="Invoices">
            <BillingInvoices />
          </RouteHeader>
        )
      },
      {
        path: "/billing/templates",
        element: (
          <RouteHeader title="Invoice Templates">
            <BillingTemplates />
          </RouteHeader>
        )
      },
      {
        path: "/billing/settings",
        element: (
          <RouteHeader title="Stripe Settings">
            <BillingStripeSettings />
          </RouteHeader>
        )
      },
      {
        path: "/page-widgets",
        element: (
          <RouteHeader title="Page Widgets">
            <PageWidgets />
          </RouteHeader>
        )
      },
      {
        path: "/proactive-engagement",
        element: (
          <RouteHeader title="Proactive Engagement">
            <ProactiveEngagement />
          </RouteHeader>
        )
      },
      {
        path: "/visitor-monitor",
        element: (
          <RouteHeader title="Visitor Monitor">
            <VisitorMonitor />
          </RouteHeader>
        )
      },
      {
        path: "/email-campaigns",
        element: (
          <RouteHeader title="Email Campaigns">
            <EmailCampaigns />
          </RouteHeader>
        )
      },
      {
        path: "/ivr-campaigns",
        element: (
          <RouteHeader title="IVR Outbound Campaigns">
            <IvrCampaigns />
          </RouteHeader>
        )
      },
      {
        path: "/admin/packages",
        element: (
          <RouteHeader title="Solution Packages">
            <Packages />
          </RouteHeader>
        )
      },
      {
        path: "/support",
        element: (
          <RouteHeader title="Contact Support">
            <ContactSupport />
          </RouteHeader>
        )
      },
      {
        path: "/reports/value",
        element: (
          <RouteHeader title="Value Report">
            <ValueReport />
          </RouteHeader>
        )
      },
      {
        path: "/reports/agent-performance",
        element: (
          <RouteHeader title="Agent Performance">
            <AgentPerformance />
          </RouteHeader>
        )
      },
      {
        path: "/reports/leaderboard",
        element: (
          <RouteHeader title="Agent Leaderboard">
            <Leaderboard />
          </RouteHeader>
        )
      },
      {
        path: "/reports/chat-intelligence",
        element: (
          <RouteHeader title="Chat Intelligence">
            <ChatIntelligence />
          </RouteHeader>
        )
      },
      {
        path: "/reports/csat",
        element: (
          <RouteHeader title="CSAT Survey Results">
            <CsatReport />
          </RouteHeader>
        )
      },
      {
        path: "/reports/conversions",
        element: (
          <RouteHeader title="Conversion Attribution">
            <ConversionReport />
          </RouteHeader>
        )
      },
      {
        path: "/settings/conversions",
        element: (
          <RouteHeader title="Conversion Goals">
            <Conversions />
          </RouteHeader>
        )
      },
      { path: "*", element: <NotFoundPage /> }
    ]
  }
];

const mainLoader = async ({ request }: any) => {
  const url = new URL(request.url);
  const route = url.pathname;
  if (AuthService.isAuthenticated() && route === "/") {
    return redirect("/");
  }
  return null;
};

const appRouter = createBrowserRouter(createAppRoutes(mainLoader));

export function AppRoutes() {
  return <RouterProvider router={appRouter} />;
}
